import { CustomLogicDefinition } from '../custom-logic'
import { DefaultActionsDetails } from '../default-actions-details-definition'
import { FieldDefinition, ListFieldDefinition } from '../field-definitions'
import { FormModel } from '../form-definitions'
import { LogicModel } from '../logic-definitions'
import { DefinitionUtils } from '../utils'
import { BaseValidationDefinition, RangeValidationDefinition } from '../validation-definitions'
import { ViewModel } from '../view-definitions/view-model'

export class EntityModel {
  public base: string
  public canBeCreated: boolean
  public canBeDeleted: boolean
  public canBeDeactivated: boolean
  public conditionalRules: LogicModel[]
  public createForm: string
  public customLogics: CustomLogicDefinition[]
  public dateRef: string
  public defaultForm: string
  public editForm: string
  public defaultRules: BaseValidationDefinition[]
  public description: string
  public fields: FieldDefinition[]
  public forms: FormModel[]
  public idml: string
  public name: string
  public parent: string
  public singleton: boolean
  public views: ViewModel[]
  public showBinnedItemsFilter: boolean
  public defaultActionsDetails: DefaultActionsDetails[]
  public extendedBy: string

  constructor(model: EntityModel) {
    this.base = model.base
    this.canBeCreated = model.canBeCreated
    this.canBeDeleted = model.canBeDeleted
    this.canBeDeactivated = model.canBeDeactivated
    this.conditionalRules = model.conditionalRules || []
    this.createForm = model.createForm
    this.customLogics = model.customLogics || []
    this.dateRef = model.dateRef
    this.defaultForm = model.defaultForm
    this.editForm = model.editForm
    this.defaultRules = model.defaultRules || []
    this.description = model.description || ''
    this.fields = model.fields ? this.mapFieldDefinitions(model.fields) : []
    this.forms = model.forms ? this.mapFormModels(model.forms) : []
    this.idml = model.idml
    this.name = model.name
    this.parent = model.parent
    this.singleton = model.singleton
    this.views = model.views ? this.mapViewModels(model.views) : []
    this.showBinnedItemsFilter = model.showBinnedItemsFilter
    this.defaultActionsDetails = model.defaultActionsDetails ?? []
    this.extendedBy = model.extendedBy
  }

  public merge(model: EntityModel): void {
    if (model.views)
      model.views.forEach((view) => {
        if (!this.views.find((v) => v.name == view.name)) {
          this.views.push(new ViewModel(view))
        }
      })

    if (model.forms)
      model.forms.forEach((form) => {
        if (!this.forms.find((f) => f.name == form.name)) {
          this.forms.push(new FormModel(form))
        }
      })
  }

  public getFieldDefinition(fieldName: string, fields?: FieldDefinition[]): FieldDefinition {
    const fieldList = fields || this.fields
    let field = fieldList.find((fld) => fld.name == fieldName)
    if (!field) {
      for (let i = 0; i < fieldList.length; i++) {
        const currentField = fieldList[i]
        if (currentField.fieldDefinitionType === 'List') {
          const listField = currentField as ListFieldDefinition
          field = this.getFieldDefinition(fieldName, listField.fields)
          if (field) break
        }
      }
    }
    return field as FieldDefinition
  }

  public getForm(formName: string): FormModel | undefined {
    return this.forms.find((form) => form.name == formName)
  }

  public getView(viewName: string): ViewModel | undefined {
    return this.views.find((view) => view.name == viewName)
  }

  public hasCustomLogic(): boolean {
    return this.customLogics.length > 0
  }

  public hasCustomActions(): boolean {
    const viewHasActions = this.views.some((v) => v.customActions.length > 0)
    const formHasActions = this.forms.some((f) => f.customActions.length > 0)
    return viewHasActions || formHasActions
  }

  public hasCustomFields(): boolean {
    return this.forms.some((f) => f.hasCustomComponents())
  }

  public getValidation(msgCode: string, fieldName: string): BaseValidationDefinition {
    const res = this.defaultRules.find((r) => r.msgCode === msgCode && r.field === fieldName)
    //TODO: remember to check for the validation in forms when they are implemented!
    return res as BaseValidationDefinition
  }

  public getNumericValidation(fieldName: string): RangeValidationDefinition {
    const res = this.defaultRules.find((r) => r.field === fieldName && r.validationType === 'Range')
    return res as RangeValidationDefinition
  }

  private mapViewModels(views: ViewModel[]): ViewModel[] {
    const mappedViews: ViewModel[] = []
    if (views) {
      views.forEach((view) => {
        mappedViews.push(new ViewModel(view))
      })
    }
    return mappedViews
  }

  private mapFormModels(forms: FormModel[]): FormModel[] {
    const mappedForms: FormModel[] = []
    if (forms) {
      forms.forEach((form) => {
        mappedForms.push(new FormModel(form))
      })
    }
    return mappedForms
  }

  private mapFieldDefinitions(fields: FieldDefinition[]): FieldDefinition[] {
    return DefinitionUtils.mapFieldDefinitions(fields)
  }
}
